import { LogOut } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { isItemActive, PRIMARY_NAV_ITEMS, SECONDARY_NAV_ITEMS, type NavItem } from './nav-config';

import { useAuthStore } from '@/shared/auth/auth.store';
import { Avatar } from '@/shared/components/ui/avatar';
import { PulseIndicator } from '@/shared/components/ui/pulse-indicator';
import { cn } from '@/shared/lib/cn';

export interface SidebarProps {
  collapsed?: boolean;
  /** Quando true, sidebar e renderizado em drawer (mobile) — omite header de logo. */
  inDrawer?: boolean;
  /** Callback disparado quando o usuario clica em qualquer NavLink. Util para fechar o drawer. */
  onNavigate?: () => void;
  testID?: string;
}

export function Sidebar({
  collapsed = false,
  inDrawer = false,
  onNavigate,
  testID = 'app-sidebar',
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const showLabels = !collapsed;

  const displayName = user?.name ?? 'Você';
  const displayStatus = user?.email ?? 'Conectado';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <aside
      data-testid={testID}
      data-collapsed={collapsed ? 'true' : 'false'}
      aria-label="Navegação primária"
      className={cn(
        'flex h-screen flex-shrink-0 flex-col bg-surface-low',
        inDrawer ? 'w-full' : 'sticky top-0',
        collapsed ? 'w-[72px]' : 'w-[240px]',
      )}
    >
      {!inDrawer ? (
        <div
          className={cn(
            'flex h-[60px] items-center border-b-0 px-4',
            collapsed && 'justify-center px-0',
          )}
        >
          <Logo collapsed={collapsed} />
        </div>
      ) : null}

      <nav className="flex-1 overflow-y-auto px-2 py-4">
        <ul className="space-y-1">
          {PRIMARY_NAV_ITEMS.map((item) => (
            <li key={item.id}>
              <SidebarNavItem
                item={item}
                active={isItemActive(location.pathname, item)}
                showLabel={showLabels}
                onNavigate={onNavigate}
              />
            </li>
          ))}
        </ul>

        <div className="my-4 h-px bg-surface-high" role="separator" />

        <ul className="space-y-1">
          {SECONDARY_NAV_ITEMS.map((item) => (
            <li key={item.id}>
              <SidebarNavItem
                item={item}
                active={isItemActive(location.pathname, item)}
                showLabel={showLabels}
                onNavigate={onNavigate}
              />
            </li>
          ))}
        </ul>
      </nav>

      <div
        className={cn(
          'flex flex-shrink-0 items-center gap-3 px-3 py-3',
          collapsed && 'flex-col justify-center px-0',
        )}
        data-testid="sidebar-footer"
      >
        <Avatar name={displayName} size="sm" testID="sidebar-avatar" />
        {showLabels ? (
          <div className="min-w-0 flex-1">
            <p className="truncate text-label-lg text-on-surface">{displayName}</p>
            <p className="truncate text-label-md text-secondary-text">{displayStatus}</p>
          </div>
        ) : null}
        <button
          type="button"
          aria-label="Sair"
          data-testid="sidebar-logout"
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-base text-secondary-text transition-colors hover:bg-surface-high hover:text-on-surface',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-low',
          )}
          onClick={handleLogout}
        >
          <LogOut size={18} aria-hidden />
        </button>
      </div>
    </aside>
  );
}

function Logo({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="flex items-center gap-2" data-testid="sidebar-logo">
      <span
        aria-hidden
        className="inline-flex h-9 w-9 items-center justify-center rounded-base bg-cta-gradient font-extrabold text-canvas shadow-fab"
      >
        G
      </span>
      {!collapsed ? <span className="text-display-sm text-on-surface">Grana</span> : null}
    </div>
  );
}

interface SidebarNavItemProps {
  item: NavItem;
  active: boolean;
  showLabel: boolean;
  onNavigate?: (() => void) | undefined;
}

function SidebarNavItem({ item, active, showLabel, onNavigate }: SidebarNavItemProps) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      data-testid={item.testID}
      data-active={active ? 'true' : 'false'}
      aria-current={active ? 'page' : undefined}
      title={showLabel ? undefined : item.label}
      onClick={onNavigate}
      className={cn(
        'group relative flex h-11 items-center gap-3 rounded-base px-3 text-label-lg transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-low',
        active
          ? 'bg-primary/10 text-primary'
          : 'text-secondary-text hover:bg-surface-high hover:text-on-surface',
        !showLabel && 'justify-center px-0',
      )}
    >
      <Icon size={20} aria-hidden className="flex-shrink-0" />
      {showLabel ? <span className="flex-1 truncate">{item.label}</span> : null}
      {active ? (
        <span className="ml-auto" aria-hidden>
          <PulseIndicator />
        </span>
      ) : null}
    </NavLink>
  );
}
